import { useState, useEffect } from 'react';
import { Star } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useProfile } from '../../contexts/ProfileContext';
import { Watchlist } from './Watchlist';
import { DashboardStockHeader } from './DashboardStockHeader';
import { TechnicalAnalysisTradingView } from '../technical-analysis/TechnicalAnalysisTradingView';
import { NewBrokerSummary } from '../broker-activity/NewBrokerSummary';
import { NewBrokerTransaction } from '../broker-activity/NewBrokerTransaction';
import { BrokerInventoryCard } from './BrokerInventoryCard';
import { DoneSummaryCard } from './DoneSummaryCard';
import { StockTransactionDoneDetail } from '../stock-transaction/StockTransactionDoneDetail';
import { OrderbookReplay } from '../stock-transaction/OrderbookReplay';
import { StoryEmitenProfile } from '../story/StoryEmitenProfile';
import { Insider } from '../market-analysis/Insider';

type TabId = 'chart' | 'broker-summary' | 'broker-transaction' | 'broker-inventory' | 'done-summary' | 'done-detail' | 'orderbook-replay' | 'emiten-profile' | 'insider';

export function Dashboard() {
  const { profile } = useProfile();
  const { showToast } = useToast();
  const [selectedStock, setSelectedStock] = useState(() => {
    try {
      const saved = localStorage.getItem('watchlist-favorites');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed[0];
        }
      }
    } catch (e) {
      // ignore
    }
    return 'BBRI';
  });
  const [activeTab, setActiveTab] = useState<TabId>('chart');
  const [mountedTabs, setMountedTabs] = useState<Set<TabId>>(new Set(['chart']));
  const [timeframe, setTimeframe] = useState('1D');

  const isUserRole = profile?.role === 'user';
  const timeframeOptions = isUserRole
    ? ['1D', '1W', '1MO']
    : ['1D', '1W', '1MO', '2W_BAZI', '1M_BAZI'];

  useEffect(() => {
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev;
      const newSet = new Set(prev);
      newSet.add(activeTab);
      return newSet;
    });
  }, [activeTab]);

  // Check for email verification success toast
  useEffect(() => {
    const showVerificationToast = localStorage.getItem('showEmailVerificationSuccessToast');
    if (showVerificationToast === 'true') {
      // Remove flag immediately
      localStorage.removeItem('showEmailVerificationSuccessToast');
      
      // Show welcome toast
      showToast({
        type: 'success',
        title: 'Email Berhasil Diverifikasi! 🎉',
        message: 'Selamat datang di BandarmoloNY! Akun Anda sudah aktif.',
      });
    }
  }, [showToast]);

  const handleStockSelect = (symbol: string) => {
    setSelectedStock(symbol);
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'chart', label: 'Chart' },
    { id: 'broker-summary', label: 'Broker Summary' },
    { id: 'broker-transaction', label: 'Broker Transaction' },
    { id: 'broker-inventory', label: 'Broker Inventory' },
    { id: 'done-summary', label: 'Trade Footprints' },
    { id: 'done-detail', label: 'Trade Detail' },
    { id: 'orderbook-replay', label: 'Bid-Offer Replay' },
    { id: 'emiten-profile', label: 'Emiten' },
    { id: 'insider', label: 'Insider' },
  ];

  return (
    <div className="h-[calc(100vh-72px)] p-2 overflow-hidden flex flex-col xl:flex-row gap-2">
      
      {/* Left Sidebar: Watchlist */}
      <div className="w-full xl:w-[275px] flex-shrink-0 h-[400px] xl:h-full">
        <Watchlist
          selectedStock={selectedStock}
          onStockSelect={handleStockSelect}
          showFavoritesOnly={false}
        />
      </div>

      {/* Right Main Content */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        
        {/* Global Stock Header Moved to Watchlist */}

        {/* Tab Navigation */}
        <div className="flex-shrink-0 flex overflow-x-auto overflow-y-hidden border border-border rounded mb-2 pb-0.5 scrollbar-hide space-x-2 bg-card">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-3 py-1.5 text-[13px] font-medium whitespace-nowrap transition-colors border-b-2 -mb-[3px] flex items-center gap-1 ${
                activeTab === tab.id 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {tab.id === 'orderbook-replay' && (
                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {mountedTabs.has('chart') && (
            <div className={`h-full w-full relative ${activeTab === 'chart' ? 'block' : 'hidden'}`}>
              <TechnicalAnalysisTradingView
                selectedStock={selectedStock}
                hideControls={false}
                styleProp="candles"
                timeframeProp={timeframe}
                showStockSymbol={false}
                showTicker={false}
                timeframeOptions={timeframeOptions}
                onTimeframeChange={(tf) => setTimeframe(tf)}
                persistenceKey="dashboard-watchlist-chart"
                enableForeignDomestic={true}
                isActive={activeTab === 'chart'}
              />
            </div>
          )}

          {mountedTabs.has('broker-summary') && (
            <div className={`h-full ${activeTab === 'broker-summary' ? 'block' : 'hidden'}`}>
              <NewBrokerSummary
                selectedStock={selectedStock}
                isDashboard={true}
                isActive={activeTab === 'broker-summary'}
              />
            </div>
          )}

          {mountedTabs.has('broker-transaction') && (
            <div className={`rounded border border-border overflow-hidden h-full ${activeTab === 'broker-transaction' ? 'block' : 'hidden'}`}>
               <NewBrokerTransaction selectedStock={selectedStock} isDashboard={true} isActive={activeTab === 'broker-transaction'} />
            </div>
          )}

          {mountedTabs.has('broker-inventory') && (
            <div className={`h-full ${activeTab === 'broker-inventory' ? 'block' : 'hidden'}`}>
              <BrokerInventoryCard
                selectedStock={selectedStock}
                defaultExpanded={true}
                isActive={activeTab === 'broker-inventory'}
              />
            </div>
          )}

          {mountedTabs.has('done-summary') && (
             <div className={`h-full ${activeTab === 'done-summary' ? 'block' : 'hidden'}`}>
               <DoneSummaryCard
                 selectedStock={selectedStock}
                 defaultExpanded={true}
                 isActive={activeTab === 'done-summary'}
               />
             </div>
          )}

          {mountedTabs.has('done-detail') && (
            <div className={`rounded border border-border overflow-hidden h-full ${activeTab === 'done-detail' ? 'block' : 'hidden'}`}>
              <StockTransactionDoneDetail sidebarOpen={false} selectedStock={selectedStock} isDashboard={true} isActive={activeTab === 'done-detail'} />
            </div>
          )}

          {mountedTabs.has('orderbook-replay') && (
             <div className={`rounded border border-border overflow-hidden h-full ${activeTab === 'orderbook-replay' ? 'block' : 'hidden'}`}>
               <OrderbookReplay selectedStock={selectedStock} isDashboard={true} isActive={activeTab === 'orderbook-replay'} />
             </div>
          )}

          {mountedTabs.has('emiten-profile') && (
            <div className={`rounded border border-border overflow-hidden h-full ${activeTab === 'emiten-profile' ? 'flex flex-col' : 'hidden'}`}>
               <StoryEmitenProfile selectedStock={selectedStock} isDashboard={true} />
            </div>
          )}

          {mountedTabs.has('insider') && (
            <div className={`rounded border border-border overflow-hidden h-full ${activeTab === 'insider' ? 'flex flex-col' : 'hidden'}`}>
               <Insider isDashboard={true} selectedStock={selectedStock} />
            </div>
          )}
        </div>
      </div>
      
    </div>
  );
}
